import React from "react";
import { cn } from "@/lib/utils";
import { EmptyState } from "@/components/ui/Display";

/**
 * Column-driven table in the admin-panel style (uppercase tracked headers,
 * gradient row hover). Render it inside a card; use TableCard for the full
 * "count header + divider + table" block.
 *
 * columns: [{ key, header, render?(row), className?, align?: "right", hide?: "md"|"lg"|"xl" }]
 */
const HIDE = { md: "hidden md:table-cell", lg: "hidden lg:table-cell", xl: "hidden xl:table-cell" };

export function DataTable({ columns, rows, rowKey = "id", empty, maxRows, onRowClick, className }) {
  if (rows.length === 0) return <EmptyState {...(typeof empty === "string" ? { title: empty } : empty)} />;
  const visible = maxRows ? rows.slice(0, maxRows) : rows;

  return (
    <div className={cn("overflow-x-auto", className)}>
      <table className="w-full text-sm">
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={cn(
                  "px-4 py-3 text-left text-[11px] font-bold text-slate-500 uppercase tracking-[0.18em] whitespace-nowrap",
                  c.align === "right" && "text-right", HIDE[c.hide],
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((row, i) => (
            <tr
              key={typeof rowKey === "function" ? rowKey(row, i) : row[rowKey]}
              onClick={onRowClick && (() => onRowClick(row))}
              className={cn("table-row-hover", onRowClick && "cursor-pointer")}
            >
              {columns.map((c) => (
                <td key={c.key} className={cn("px-4 py-3 text-slate-700", c.align === "right" && "text-right", HIDE[c.hide], c.className)}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {maxRows && rows.length > maxRows && (
        <p className="text-[11px] text-slate-400 text-center py-3">Showing {maxRows} of {rows.length}</p>
      )}
    </div>
  );
}

/** Card with a bold count line, optional right-side note/action, a divider, then the table. */
export function TableCard({ heading, aside, children, className }) {
  return (
    <div className={cn("neu-card overflow-hidden", className)}>
      {(heading || aside) && (
        <>
          <div className="px-5 py-4 flex items-center justify-between gap-2 flex-wrap">
            <p className="text-sm font-extrabold text-brand-blue">{heading}</p>
            {aside && <div className="flex items-center gap-2 text-xs text-slate-500">{aside}</div>}
          </div>
          <div className="neu-divider mx-5 mb-1" />
        </>
      )}
      {children}
    </div>
  );
}

/** Two-line cell: bold primary text over muted secondary text. */
export function PersonCell({ name, sub }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-bold text-brand-blue truncate">{name}</p>
      {sub && <p className="text-[11px] text-slate-500 truncate">{sub}</p>}
    </div>
  );
}
